import { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import { MessageItem } from "./MessageItem";
import { flashMessage } from "./MessageList";
import { Composer } from "./Composer";
import { useChat } from "@/lib/chat";
import { pluralize } from "@/lib/format";

export const ThreadPanel = ({ parentId }) => {
  const { threads, closePanel, sendMessage, usersByHandle, usersById, meId, openProfile, conversations, jump, clearJump, prefs } = useChat();
  const t = threads[parentId];
  const ctx = useMemo(() => ({ usersByHandle, usersById, meId, largeEmoji: prefs.large_emoji, onMentionClick: (u) => openProfile(u.id) }), [usersByHandle, usersById, meId, openProfile, prefs.large_emoji]);
  const conv = t?.conversation || conversations.find((c) => c.id === t?.parent?.conversation_id);
  const handled = useRef(null);
  useEffect(() => {
    if (!jump || jump.pending || jump.parentId !== parentId || handled.current === jump.mid) return;
    if (!t?.replies?.some((r) => r.id === jump.mid)) return;
    handled.current = jump.mid;
    requestAnimationFrame(() => { flashMessage(jump.mid); setTimeout(clearJump, 2600); });
  }, [jump, t, parentId, clearJump]);
  return (
    <aside className="tc-panel" data-testid="thread-panel">
      <div className="tc-panel__header">
        <span className="tc-panel__title">Thread</span>
        {conv && <span className="tc-panel__subtitle">{conv.type === "channel" ? `# ${conv.name || conv.display_name}` : conv.display_name}</span>}
        <button className="tc-iconbtn tc-panel__close" onClick={closePanel} title="Close" data-testid="thread-close-button"><X size={18} /></button>
      </div>
      <div className="tc-panel__body">
        {!t ? (
          <div className="tc-empty"><p>Loading thread…</p></div>
        ) : (
          <>
            <div className="tc-scroll" style={{ flex: 1, paddingTop: 8 }} data-testid="thread-messages">
              <MessageItem m={{ ...t.parent, is_saved: t.parent.is_saved }} ctx={ctx} inThread hideThreadIndicator />
              {t.replies.length > 0 && <div className="tc-thread__sep">{pluralize(t.replies.length, "reply").replace("replys", "replies")}</div>}
              {t.replies.map((r, i) => <MessageItem key={r.id} m={r} ctx={ctx} inThread grouped={i > 0 && t.replies[i - 1].user_id === r.user_id && new Date(r.created_at) - new Date(t.replies[i - 1].created_at) < 300000} />)}
            </div>
            <Composer conversationId={t.parent.conversation_id} parentId={parentId} placeholder="Reply…" inPanel compact autoFocus onSend={(text, fileIds) => sendMessage(t.parent.conversation_id, text, parentId, fileIds)} />
          </>
        )}
      </div>
    </aside>
  );
};
